// InvoiceQueuePage.tsx — the AP triage queue (chunk 2, phase 4).
//
// Built to the redline Mannie marked up (.lavish/ap-queue-mockups.html): one
// queue, a "No order" lane chip, first-time-vendor rows loud, everything two
// keys from handled. Rapid entry is the floor: J/K down the pile, Enter opens,
// D starts a discard, upload never blocks the list.
//
// The page composes and styles nothing about its own top. PageHeader carries
// the title, the ? drawer, the one filled action and the Tools menu;
// FilterBar carries the lane switch and the count; Segmented carries both
// switches. The paragraph this page used to print under its title is in the
// drawer, where an explanation belongs — see docs/features/page-shell.md.
//
// The three shared-table seams this page was built around are now consumed:
// the table is components/table/DataTable, columns are tanstack ColumnDefs,
// and the row keyboard is the component's own dialect (the local hook is gone).
// Loading and empty are the component's `emptyMode="replace"`: ONE DataTable
// renders always, and it swaps its field's contents instead of the page
// swapping the table for a box. The queue still never shows a header over an
// empty pile, and the always-mounted table fixes two things the old ternary
// broke — the landing charm never fired here (unmounting per fetch wiped the
// seen-ids the latch compares against) and the selection jumped back to the
// first row on every refetch.

import Appear from '../components/ui/Appear';
import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useToast } from '../components/Toast/ToastContext';
import { formatEventDate } from '../utils/date-utils';
import { DataTable, DataTableHandle } from '../components/table/DataTable';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FilterBar, FilterEnd, Segmented } from '../components/FilterBar';
import type { MenuItem } from '../components/ui/Menu';
import Tag, { TagTone } from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { useFilePicker } from '../components/ui/FilePick';
import {
  useApInvoicesApi, InvoiceQueueRow, formatCents, centsToDollars,
} from './apInvoicesApi';

type Lane = 'all' | 'no_order' | 'flagged';

// Which pile is on screen. 'books' is the confirmed family as ONE story:
// confirmed (owed or kept out), delivered, delivery_failed — a failed send
// hiding in a view nobody opens would violate the loud-failure rule.
type QueueView = 'review' | 'books' | 'discarded';
const VIEW_STATUS: Record<QueueView, string> = {
  review: 'needs_review',
  books: 'confirmed,delivered,delivery_failed',
  discarded: 'discarded',
};

const LANES: Array<{ key: Lane; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'no_order', label: 'No order' },
  { key: 'flagged', label: 'Flagged' },
];

// The three piles, in the order a person meets them: what needs working, what
// has been confirmed, what was thrown away.
const VIEWS: Array<{ key: QueueView; label: string }> = [
  { key: 'review', label: 'Review pile' },
  { key: 'books', label: 'Sent to books' },
  { key: 'discarded', label: 'Discarded' },
];

const laneFilter = (lane: Lane) => (row: InvoiceQueueRow): boolean => {
  if (lane === 'no_order') return row.match_method === null;
  if (lane === 'flagged') return row.needs_attention;
  return true;
};

const hasReason = (row: InvoiceQueueRow, code: string) =>
  (row.attention_reasons || []).some((r) => r.code === code);

type PillTone = 'gold' | 'red' | 'green' | 'gray' | 'blue';

const PILL_TONES: Record<PillTone, TagTone> = {
  gold: 'warn', red: 'bad', green: 'good', blue: 'info', gray: 'neutral',
};

const Pill = ({ tone, children }: { tone: PillTone; children: React.ReactNode }) => (
  <Tag size="sm" mono tone={PILL_TONES[tone]} className="mr-1 mb-0.5">{children}</Tag>
);

/**
 * Why a row is flagged, as one list read twice: the cell renders it as pills,
 * Export CSV joins the words with "; ". Two implementations of this would drift
 * the day a flag is added, and a file that disagrees with the screen about why
 * an invoice is flagged is worse than no file at all.
 *
 * `title` is the sentence a pill carries on hover; it stays on a wrapping span,
 * never on the pill itself, and never reaches the export.
 */
interface FlagChip { word: string; tone: PillTone; title?: string }

const flagChips = (r: InvoiceQueueRow): FlagChip[] => {
  const chips: FlagChip[] = [];
  if (r.status === 'delivered') chips.push({ word: 'forwarded', tone: 'green' });
  if (r.status === 'delivery_failed') {
    chips.push({ word: 'send failed', tone: 'red', title: r.delivery_error || undefined });
  }
  if (r.status === 'confirmed' && r.delivery_intended && !r.delivered_at && !r.delivery_attempted_at) {
    chips.push({ word: 'forward owed', tone: 'gold' });
  }
  if (r.status === 'confirmed' && r.delivery_intended && !r.delivered_at && r.delivery_attempted_at) {
    chips.push({
      word: 'outcome unknown',
      tone: 'red',
      title: 'A send attempt is on record and its outcome is unknown. Check QuickBooks before retrying.',
    });
  }
  if (r.status === 'confirmed' && !r.delivery_intended) chips.push({ word: 'kept out of books', tone: 'gray' });
  if (r.vendor_id === null) chips.push({ word: 'unknown vendor', tone: 'red' });
  if (hasReason(r, 'appears_card_paid')) chips.push({ word: 'card-paid', tone: 'gold' });
  if (hasReason(r, 'po_vendor_mismatch')) chips.push({ word: 'vendor mismatch', tone: 'red' });
  if (hasReason(r, 'duplicate_file') || hasReason(r, 'duplicate_number') || r.likely_duplicate_of) {
    chips.push({ word: 'possible duplicate', tone: 'gold' });
  }
  if (hasReason(r, 'bills_beyond_received')) chips.push({ word: 'bills beyond received', tone: 'gold' });
  // Neutral, and beside the gold one rather than instead of it: an invoice can
  // both bill past what arrived AND be waiting on a return. A return in
  // progress is not somebody's mistake, so it must not read like one.
  if (hasReason(r, 'return_pending')) chips.push({ word: 'return in progress', tone: 'gray' });
  return chips;
};

const vendorLabel = (r: InvoiceQueueRow) =>
  r.vendor_name || r.vendor_observed || 'Unknown vendor';

/** The Order cell in words. Same two facts, same order, spelled for a cell that
 *  cannot hold a colored pill. */
const orderText = (r: InvoiceQueueRow): string => {
  const unknown = hasReason(r, 'unknown_po_number') ? "names PO we don't hold" : '';
  if (r.match_method) {
    const matched = `matched (${r.matched_line_count}/${r.line_count} lines)`;
    return unknown ? `${matched}; ${unknown}` : matched;
  }
  return unknown || 'no order';
};

// Module-scope on purpose (the DataTable contract): every cell is pure over
// the row. No sorting — the server orders the pile (created_at desc).
//
// `meta.csv` is what Export CSV writes: the screen's own words, money as a
// bare dollar number a sheet can sum, dates raw so they sort as dates.
const QUEUE_COLUMNS: ColumnDef<InvoiceQueueRow, any>[] = [
  {
    id: 'vendor',
    header: 'Vendor',
    meta: { csv: (r) => vendorLabel(r) },
    cell: ({ row }) => (
      <div>
        <div className="font-semibold text-on-surface">
          {vendorLabel(row.original)}
        </div>
        <div className="font-mono text-[11px] text-on-surface-variant truncate max-w-[240px]">
          {row.original.original_filename || row.original.source}
        </div>
      </div>
    ),
  },
  {
    id: 'invoice',
    header: 'Invoice',
    // The number alone: a bookkeeper matches this column against QuickBooks,
    // and a parenthetical would break every one of those lookups. Credit-memo
    // status reaches the file as the sign on Amount, which is how a credit
    // reads in a ledger anyway.
    meta: { csv: (r) => r.invoice_number ?? '' },
    cell: ({ row }) => (
      <span className="font-mono text-[13px] text-on-surface whitespace-nowrap">
        {row.original.invoice_number || '—'}
        {row.original.is_credit_memo && <Pill tone="blue">credit memo</Pill>}
      </span>
    ),
  },
  {
    id: 'date',
    header: 'Date',
    // Raw YYYY-MM-DD: a sheet reads it as a date and sorts it as one, where
    // "Jul 27, 2026" is text that sorts alphabetically.
    meta: { csv: (r) => r.invoice_date ?? '' },
    cell: ({ row }) => (
      <span className="text-on-surface-variant">
        {row.original.invoice_date ? formatEventDate(row.original.invoice_date) : '—'}
      </span>
    ),
  },
  {
    id: 'total',
    header: 'Amount',
    meta: { align: 'right', csv: (r) => centsToDollars(r.total_cents) },
    cell: ({ row }) => (
      <span className={`font-mono tabular-nums ${Number(row.original.total_cents) < 0 ? 'text-sky-300' : 'text-on-surface'}`}>
        {formatCents(row.original.total_cents)}
      </span>
    ),
  },
  {
    id: 'order',
    header: 'Order',
    meta: { csv: (r) => orderText(r) },
    cell: ({ row }) => {
      if (row.original.match_method) {
        // Matched AND names-a-PO-we-don't-hold can both be true (a typo'd
        // extra number, or a hint refused over the fake shape): a green pill
        // must never spend the red signal, so both render.
        return (
          <>
            <Pill tone="green">
              matched · {row.original.matched_line_count}/{row.original.line_count} lines
            </Pill>
            {hasReason(row.original, 'unknown_po_number') && (
              <Pill tone="red">names PO we don't hold</Pill>
            )}
          </>
        );
      }
      if (hasReason(row.original, 'unknown_po_number')) {
        return <Pill tone="red">names PO we don't hold</Pill>;
      }
      return <Pill tone="gray">no order</Pill>;
    },
  },
  {
    id: 'flags',
    header: 'Flags',
    meta: { csv: (r) => flagChips(r).map((f) => f.word).join('; ') },
    cell: ({ row }) => (
      <>
        {flagChips(row.original).map((f) => (
          f.title
            ? <Tooltip key={f.word} label={f.title}><span><Pill tone={f.tone}>{f.word}</Pill></span></Tooltip>
            : <Pill key={f.word} tone={f.tone}>{f.word}</Pill>
        ))}
      </>
    ),
  },
];

const getRowId = (r: InvoiceQueueRow) => String(r.id);

export default function InvoiceQueuePage() {
  const api = useApInvoicesApi();
  const history = useHistory();
  const toast = useToast();

  const [rows, setRows] = useState<InvoiceQueueRow[]>([]);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lane, setLane] = useState<Lane>('all');
  const [view, setView] = useState<QueueView>('review');
  const [uploading, setUploading] = useState(false);
  // Drag-and-drop is the page container's, deliberately OUTSIDE the DataTable
  // (its rows are the component's territory). The depth counter survives the
  // enter/leave churn of dragging across child elements.
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const tableRef = useRef<DataTableHandle>(null);

  const load = useCallback(async (status: string) => {
    setLoading(true);
    try {
      const res = await api.list(status);
      setRows(res.invoices);
      setFailedCount(res.delivery_failed_count || 0);
    } catch (e: any) {
      toast.error(e?.message || 'The queue could not be loaded.');
    } finally {
      setLoading(false);
    }
    // api/toast are stable context handles; re-running on their identity would refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A view switch starts from the loading look; a refetch after an action keeps the rows (quiet refresh).
  useEffect(() => { setRows([]); load(VIEW_STATUS[view]); }, [view, load]);

  const visible = useMemo(
    () => (view === 'review' ? rows.filter(laneFilter(lane)) : rows),
    [rows, lane, view],
  );

  // The Flagged lane's badge counts the whole pile, not the lane's own rows:
  // it is the reason to go and look, so it has to be readable from a lane that
  // is hiding those rows.
  const flaggedCount = useMemo(() => rows.filter((r) => r.needs_attention).length, [rows]);

  const resetSelection = () => tableRef.current?.resetSelection();

  // Export CSV asks the TABLE for what it is showing, so the file is the
  // screen: this pile, this lane, these columns, this order. The hint says the
  // count out loud, because whether the filters came along is the one thing a
  // person cannot check after the download.
  const tools: MenuItem[] = [
    {
      label: 'Export CSV',
      hint: `${visible.length} row${visible.length === 1 ? '' : 's'}, current filters`,
      disabled: loading || visible.length === 0,
      onSelect: () => {
        // null: the pile was still loading and nothing was written.
        const count = tableRef.current?.exportCsv('invoices');
        if (count == null) {
          toast.error('The pile is still loading. Try again in a moment.');
          return;
        }
        toast.success(`Exported ${count} row${count === 1 ? '' : 's'}.`);
      },
    },
  ];

  const openRow = useCallback((row: InvoiceQueueRow) => {
    history.push(`/invoices/${row.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // D lands on the review screen with the reason box already armed: a discard
  // needs a reason, and the reason box lives there. Only a review-pile row can
  // be discarded, so the key stays inert in the books and discarded views.
  const discardRow = useCallback((row: InvoiceQueueRow) => {
    if (row.status === 'needs_review') history.push(`/invoices/${row.id}`, { discard: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowKeyboard = useMemo(
    () => ({ onOpen: openRow, onDiscard: discardRow }),
    [openRow, discardRow],
  );

  // Selection and hover styling belong to the DataTable now; the page adds
  // only its domain facts — the row's DOM hook and the click-through.
  const rowProps = useCallback(
    (row: InvoiceQueueRow) => ({
      'data-testid': `invoice-row-${row.id}`,
      onClick: () => history.push(`/invoices/${row.id}`),
      className: 'cursor-pointer',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onUpload = async (file: File) => {
    setUploading(true);
    try {
      const res = await api.upload(file);
      toast.success(`Read ${res.lines.length} line${res.lines.length === 1 ? '' : 's'} from ${res.invoice.vendor_observed || 'the document'}.`);
      await load('needs_review');
      setView('review');
    } catch (e: any) {
      // The 422 carries what the document read as instead; say it.
      const reason = e?.data?.error?.document_reason;
      toast.error(reason ? `Not stored: that reads as ${reason}.` : (e?.data?.error?.message || e?.message || 'Upload failed.'));
    } finally {
      setUploading(false);
    }
  };

  const picker = useFilePicker({
    accept: 'application/pdf,image/jpeg,image/png',
    onFiles: (files) => { onUpload(files[0]); },
    inputProps: { 'data-testid': 'invoice-file-input' },
  });

  // While this page is mounted, a drop that MISSES the container must not
  // navigate the tab to the PDF (which would also drop the in-memory access
  // token). Window-level preventDefault covers the margins and the chrome;
  // the same listeners reset the overlay when a drag ends outside the window,
  // which nothing else reports.
  useEffect(() => {
    const swallow = (e: DragEvent) => { e.preventDefault(); };
    const reset = () => { dragDepth.current = 0; setDragging(false); };
    const swallowAndReset = (e: DragEvent) => { e.preventDefault(); reset(); };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallowAndReset);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallowAndReset);
      window.removeEventListener('dragend', reset);
    };
  }, []);

  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (uploading) {
      toast.error('Still reading the last one; drop it again in a moment.');
      return;
    }
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Invoices arrive as PDF, JPEG or PNG.');
      return;
    }
    onUpload(file);
  };

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 lg:pt-6">
    <div
      className={`relative ${dragging ? 'rounded-lg ring-2 ring-primary/70' : ''}`}
      data-testid="invoice-dropzone"
      onDragEnter={(e) => { if (hasFiles(e)) { dragDepth.current += 1; setDragging(true); } }}
      onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); }}
      onDragOver={(e) => { if (hasFiles(e)) e.preventDefault(); }}
      onDrop={onDrop}
    >
      <Appear show={dragging} as="div" className="absolute inset-0 z-10 pointer-events-none rounded-lg bg-black/60 flex items-center justify-center">
        <span className="font-mono text-sm text-primary border border-primary/70 border-dashed rounded-lg px-6 py-4">
          Drop the invoice to read it
        </span>
      </Appear>

      {/* The stack lives in its own element so the drag overlay above stays a
          bare absolute child: a `space-y` rule would give it a top margin and
          shift the inset box it is positioned by. */}
      <div className="space-y-4">
        <PageHeader
          title="Invoices"
          helpTitle="About invoices"
          lede="Vendor bills in review: nothing here has moved money, and confirming is what does."
          primary={
            <PrimaryAction onClick={picker.open} disabled={uploading}>
              {uploading ? 'Reading…' : 'Upload invoice'}
            </PrimaryAction>
          }
          tools={tools}
          help={
            <>
              <p>
                Every vendor bill lands here first. Nothing on this page has touched the
                books: an invoice waits in the <span className="text-on-surface">review pile</span>{' '}
                until somebody opens it, checks what it bills, and confirms it. You can also
                drop a PDF, JPEG or PNG anywhere on this page to read one in.
              </p>
              <p>
                Confirming is the act that counts. It rolls the billed amount onto the
                purchase order and, unless you say otherwise at the time, forwards a summary
                to QuickBooks. <span className="text-on-surface">Sent to books</span> then
                holds everything confirmed whatever became of that forward, so a failed send
                is never hiding in a pile nobody opens.{' '}
                <span className="text-on-surface">Discarded</span> holds the documents that
                never were, each with the reason somebody typed.
              </p>
              <p>
                In the review pile the lanes narrow what you see:{' '}
                <span className="text-on-surface">No order</span> is what nothing has matched
                to a purchase order, <span className="text-on-surface">Flagged</span> is what
                the matcher wants a person to look at. The keyboard runs the whole queue: J
                and K move down and up, Enter opens the selected invoice, and D takes it to
                its review screen with the discard reason already armed.
              </p>
            </>
          }
        />

        {/* The file input is the page's, not the header's: the button in the
            header only clicks it. Same accept list as the drop handler. */}
        {picker.input}

        {/* WHICH PILE. Navigation, not a filter: it changes which list is on
            screen rather than narrowing one, so it sits above the bar rather
            than inside it. The failed-send count rides the tab rather than a
            row, so a failure is visible from a pile nobody has opened.

            "Back to review pile" is gone with the old tabs: the track IS the
            way back, and a control that repeats what is already on screen stops
            earning its place. */}
        <Segmented
          label="Which invoices to show"
          options={VIEWS.map((v) => ({
            key: v.key,
            label: v.label,
            badge: v.key === 'books' ? failedCount : undefined,
            // A failed send is not a count, it is a failure waiting on a
            // human; the Flagged lane below wears the routine gold.
            badgeTone: v.key === 'books' ? 'alert' : undefined,
          }))}
          value={view}
          onChange={(k) => { setView(k as QueueView); resetSelection(); }}
        />

        {/* WHICH SLICE of it. The lanes narrow one pile, so they are the bar's;
            the count stays in every view, because how big a pile is is a fact
            about all three. */}
        <FilterBar>
          {view === 'review' && (
            <Segmented
              label="Which part of the review pile"
              options={LANES.map((l) => ({
                key: l.key,
                label: l.label,
                badge: l.key === 'flagged' ? flaggedCount : undefined,
              }))}
              value={lane}
              onChange={(k) => { setLane(k as Lane); resetSelection(); }}
            />
          )}
          <FilterEnd>
            {/* The loud-failure rule, in words a person can read without
                hovering: the badge says how many, this says what it means. */}
            <Appear show={failedCount > 0} className="text-danger font-medium">
              {failedCount} send{failedCount === 1 ? '' : 's'} failed and waiting on a human
            </Appear>
            <span role="status" aria-live="polite">
              {visible.length} invoice{visible.length === 1 ? '' : 's'}
            </span>
          </FilterEnd>
        </FilterBar>

        <DataTable<InvoiceQueueRow>
          ref={tableRef}
          data={visible}
          columns={QUEUE_COLUMNS}
          getRowId={getRowId}
          stickyHeader
          rowKeyboard={rowKeyboard}
          strip={
            <span className="hidden md:inline font-mono text-[11px] text-on-surface-variant tracking-wide">
              <kbd className="border border-outline-variant rounded px-1">J</kbd>
              <kbd className="border border-outline-variant rounded px-1 ml-0.5">K</kbd> move
              <kbd className="border border-outline-variant rounded px-1 ml-2">⏎</kbd> open
              <kbd className="border border-outline-variant rounded px-1 ml-2">D</kbd> discard
            </span>
          }
          emptyMode="replace"
          loading={loading}
          loadingContent={(
            <SkeletonGroup label="Loading the pile" className="space-y-2 p-4">
              {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </SkeletonGroup>
          )}
          empty={
            <EmptyState
              variant="area"
              title={view === 'discarded' ? 'Nothing has been discarded.'
                : view === 'books' ? 'Nothing has been confirmed yet.'
                  : lane === 'all' ? 'The pile is empty.' : 'Nothing in this lane.'}
            >
              {view === 'discarded'
                ? 'Discards land here, each with its reason.'
                : view === 'books'
                  ? 'Confirmed invoices live here, each wearing where its forward stands.'
                  : 'Upload a vendor invoice and it arrives read, matched, and ready to review.'}
            </EmptyState>
          }
          rowProps={rowProps}
        />
      </div>
    </div>
    </PageLayout>
  );
}
